/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import * as authSelectors from "../auth/authSelectors";
import { useParams } from "react-router-dom";
import { css } from "@emotion/react";
import {
  useDeleteShopPaymentProvider,
  useGetPaymentProviders,
} from "./service/shop-api";
import { SpinnerBlock } from "../common/Spinner";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { EmptyState } from "../../components/EmptyState";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { TableBody } from "../../components/table-components/TableBody";
import { colors } from "../../components/colors";
import { routes } from "../../appRoutingHelpers";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellLink } from "../../components/table-components/TableCellLink";
import { StyledText } from "../../assets/typography/typography";
import { TableEllipsisDropdown } from "../../components/TableEllipsisDropdown";
import { DropdownItemLink } from "../common/DropdownItemLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ButtonInternalLink } from "../../components/NewButtons";
import { DropdownItemButton } from "../common/DropdownItemButton";
import { toastr } from "react-redux-toastr";

// Hacking width until all cards on page are updated
const cardAdditionalStyling = css`
  max-width: 1174px !important;
`;

// Normally we would use PageWrapper component, but other components on the page are not updated yet
const cardWrapperStyle = css`
  padding-left: 24px;
  padding-right: 24px;
  display: flex;
  width: 100%;
  justify-content: center;
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  widht: 100%;
`;

export const ShopPaymentProvidersList = () => {
  let { shopId } = useParams<{
    shopId: string | undefined;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { data, isLoading, error } = useGetPaymentProviders({
    organizationKeyName,
    shopId: shopId!,
  });

  const {
    mutateAsync: deletePaymentProvider,
    isLoading: isDeletingPaymentProvider,
  } = useDeleteShopPaymentProvider({
    shopId: shopId!,
    organizationKeyName,
  });

  useHandleFetchError(error);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <div css={cardWrapperStyle}>
      <CardContainer additionalStyling={cardAdditionalStyling}>
        <PageHeader title="Payment Providers">
          <ButtonInternalLink
            to={routes.shopPaymentProviderAdd({
              organizationKey: organizationKeyName,
              shopId: shopId!,
            })}
          >
            Add new payment provider
          </ButtonInternalLink>
        </PageHeader>
        <div css={wrapperStyle}>
          {data && data?.length === 0 ? (
            <EmptyState
              title="No payment providers"
              subtitle="Add a payment provider to get started"
            />
          ) : (
            <>
              <Table>
                <thead>
                  <TableRowHeader>
                    <TableCellHeaderText>Provider</TableCellHeaderText>
                    <TableCellHeaderText>Enabled</TableCellHeaderText>
                    <TableCellHeaderText>Payment methods</TableCellHeaderText>
                    <TableCellHeaderText>
                      Payment provider id
                    </TableCellHeaderText>
                    <TableCellHeaderText>Statement text</TableCellHeaderText>
                    <th />
                  </TableRowHeader>
                </thead>
                <TableBody>
                  {(data || []).map((provider, i) => {
                    const href = routes.shopPaymentProviderUpdate({
                      organizationKey: organizationKeyName,
                      paymentProviderId: provider.id,
                      shopId: shopId!,
                    });

                    return (
                      <TableRow key={i}>
                        <TableCellLink href={href} className="pl-5">
                          <StyledText
                            className="bodyTextRegular mb-0"
                            color={colors.textPrimary}
                          >
                            {provider.provider.paymentProvider ===
                            "adyen_unverified"
                              ? "SumUp"
                              : provider.provider.paymentProvider || "-"}
                          </StyledText>
                        </TableCellLink>
                        <TableCellLink href={href}>
                          <StyledText
                            className="bodyTextRegular mb-0"
                            color={colors.textPrimary}
                          >
                            {provider.enabled ? "Enabled" : "Disabled"}
                          </StyledText>
                        </TableCellLink>
                        <TableCellLink href={href}>
                          <StyledText
                            className="bodyTextRegular mb-0"
                            color={colors.textPrimary}
                          >
                            {provider.paymentMethods.join(", ")}
                          </StyledText>
                        </TableCellLink>
                        <TableCellLink href={href}>
                          <StyledText
                            className="bodyTextRegular mb-0"
                            color={colors.textPrimary}
                          >
                            {provider.paymentProviderId || "-"}
                          </StyledText>
                        </TableCellLink>
                        <TableCellLink href={href}>
                          <StyledText
                            className="bodyTextRegular mb-0"
                            color={colors.textPrimary}
                          >
                            {provider.statementText || "-"}
                          </StyledText>
                        </TableCellLink>
                        <TableEllipsisDropdown
                          menu={{
                            items: [
                              {
                                key: "1",
                                label: (
                                  <DropdownItemLink
                                    icon={
                                      <FontAwesomeIcon icon={solid("pen")} />
                                    }
                                    to={href}
                                  >
                                    Edit
                                  </DropdownItemLink>
                                ),
                              },
                              {
                                key: "2",
                                label: (
                                  <DropdownItemButton
                                    icon={
                                      <FontAwesomeIcon icon={solid("trash")} />
                                    }
                                    disabled={isDeletingPaymentProvider}
                                    onClick={async () => {
                                      try {
                                        await deletePaymentProvider({
                                          organizationKeyName,
                                          shopId: shopId!,
                                          id: provider.id,
                                        });
                                        toastr.success(
                                          "Success",
                                          "Payment provider deleted"
                                        );
                                      } catch (error) {
                                        const errorMessage = getErrorMessage(
                                          error
                                        );
                                        toastr.error("Error", errorMessage);
                                      }
                                    }}
                                  >
                                    Delete
                                  </DropdownItemButton>
                                ),
                              },
                            ],
                          }}
                        />
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </CardContainer>
    </div>
  );
};
